import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import createStudioEditor from '@grapesjs/studio-sdk';
import { firstValueFrom } from 'rxjs';
import { advanceSearchService } from 'src/app/services/advance-search';
import { layoutServices } from 'src/app/services/layout.service';
import { templateServices } from 'src/app/services/template.service';
import { environment } from 'src/environments/environment';
import { rteTinyMce } from '@grapesjs/studio-sdk-plugins';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { SettingsDialogComponent } from '../default-template-fall/default-template-fall.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-email-editor',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    PickerComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressBarModule
  ],
  templateUrl: './email-editor.component.html',
  styleUrl: './email-editor.component.scss',
  encapsulation: ViewEncapsulation.Emulated
})
export class EmailEditorComponent implements OnInit, OnDestroy {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  private editor: any;
  private linkElement!: HTMLLinkElement;
  isLoading: boolean = true;
  editorInitialized: boolean = false;

  leadTags: any = [];
  otherFields: any = [];
  recent: any = [];
  allFields = [];
  private applyTimer?: number;
  private loadingTimeout?: number;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private advanceSearch: advanceSearchService,
    private templateService: templateServices,
    private layoutService: layoutServices,
    private dialog: MatDialog
  ) {
  }

  async loadTags() {
    try {
      const response = await firstValueFrom(
        this.templateService.get({}, 'fields'),
      );

      this.leadTags = [];
      this.otherFields = [];

      this.recent = response.recent;
      this.allFields = response.all;
      response.all.forEach((item: any) => {
        if (item.entity === 'leads' && !item.entitySearch) {
          this.leadTags.push(item);
        } else {
          this.otherFields.push(item);
        }
      });
      await this.createEditor();
    } catch (error) {
      console.error('Error loading tags:', error);
      this.isLoading = false;
    }
  }

  async createEditor() {
    try {
      this.linkElement = this.renderer.createElement('link');
      this.linkElement.rel = 'stylesheet';
      this.linkElement.href = 'assets/scss/grapejs.css';
      this.renderer.appendChild(document.head, this.linkElement);
      
      // Initialize GrapesJS editor for MJML
      this.editor = await createStudioEditor({
        licenseKey: environment.grapeJsKey,
        root: this.editorContainer.nativeElement,
        layout: {
          default: {
            type: 'column',
            style: { height: '100%' },
            children: [
              {
                type: 'row',
                style: { height: '100%' },
                children: [
                  { type: 'sidebarLeft' }, 
                  {
                    type: 'column',
                    style: { flexGrow: 1 },
                    children: [
                      { type: 'sidebarTop' },
                      { type: 'canvas' },
                      // Container for the fixed RTE toolbar
                      { type: 'row', className: 'rteContainer', style: { justifyContent: 'center' } }
                    ]
                  }, 
                  { type: 'sidebarRight' }
                ]
              },
              { type: 'row', children: 'Footer text' }
            ]
          }
        },
        assets: {
          storageType: 'self',
          onUpload: async ({ files, editor }) => {
            if (!files) {
              return [];
            }
          
            const fileArray = Array.isArray(files) ? files : [files];
            if (fileArray.length === 0) {
              return [];
            }
          
            const formData = new FormData();
            fileArray.forEach((file) => {
              formData.append('file', file);
            });
            const results: any = await firstValueFrom(
              this.layoutService.uploadFiles(formData)
            );
            
            return results.map((row: any) => {
              return {
                id: row.id,
                src: row.src,
                name: row.name,
                mimeType: row.mimeType,
                size: row.size
              }
            })
          },
          onDelete: async ({ assets, editor }) => {
            const file: any = assets[0];
            await firstValueFrom(
              this.layoutService.deleteFile(encodeURIComponent(file.id))
            );
          }
        },

        plugins: [
          rteTinyMce.init({
            enableOnClick: true,
            loadConfig: ({ component, config }: { component: any; config: any }) => {
              const demoRte = component.get('demorte');
              if (demoRte === 'fixed') {
                return {
                  toolbar:
                    'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | link image media',
                  fixed_toolbar_container_target: document.querySelector('.rteContainer')
                };
              } else if (demoRte === 'quickbar') {
                return {
                  plugins: `${config.plugins} quickbars`,
                  toolbar: false,
                  quickbars_selection_toolbar: 'bold italic underline strikethrough | quicklink image'
                };
              }
              return {};
            }
          })
        ],
        
        storage: {
          type: 'self',
          autosaveChanges: 5,
          autosaveIntervalMs: 10000,
          onSave: async ({ project }) => {
            // Handle saving
          },
        },
        
        // Changed to 'email' project type for MJML editing
        project: {
          type: 'email',
          // The default project to use for new projects (MJML)
          default: {
            pages: [
              { 
                component: `
                  <mjml>
                    <mj-head>
                      <mj-attributes>
                        <mj-all font-family="Arial, sans-serif" />
                        <mj-text font-size="14px" color="#333333" line-height="1.6" />
                        <mj-section background-color="#ffffff" />
                      </mj-attributes>
                    </mj-head>
                    <mj-body background-color="#f4f4f4">
                      <mj-section background-color="#4361ee" padding="20px">
                        <mj-column>
                          <mj-text align="center" color="#ffffff" font-size="24px" font-weight="bold">
                            Email Template
                          </mj-text>
                        </mj-column>
                      </mj-section>
                      <mj-section padding="20px">
                        <mj-column>
                          <mj-text>
                            Start building your email template here...
                          </mj-text>
                        </mj-column>
                      </mj-section>
                      <mj-section background-color="#f8f9fa" padding="20px">
                        <mj-column>
                          <mj-text align="center" font-size="12px" color="#666666">
                            Footer content
                          </mj-text>
                        </mj-column>
                      </mj-section>
                    </mj-body>
                  </mjml>
                ` 
              },
            ]
          },
        },
        
        templates: {
          onLoad: async () => {
            const response = await firstValueFrom(
              this.layoutService.getTemplates()
            );
            return [...response.result];
          }
        },
        
        onEditor: async (editor) => {
          this.editor = editor;


          // Helper function to extract YouTube ID
// Helper function to extract YouTube ID with better error handling
function getYouTubeId(url) {
  if (!url) return null;
  
  // Clean the URL
  url = url.trim();
  
  // Extract video ID from various YouTube URL formats
  const patterns = [
    // Standard watch URL with v parameter
    /[?&]v=([^&\n?#]+)/,
    // Short youtu.be URL - updated pattern
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    // Direct ID after youtu.be/
    /^(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    // Embed URL
    /youtube\.com\/embed\/([^&\n?#]+)/,
    // YouTube Music
    /music\.youtube\.com\/watch\?v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // If no pattern matches but it looks like just an ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
    return url;
  }
  
  return null;
}

// Custom YouTube component with better debugging
editor.Components.addType('mj-youtube', {
  extend: 'mj-image',
  
  isComponent(el) {
    return el && el.nodeType === 1 && el.getAttribute && el.getAttribute('data-youtube') === 'true';
  },
  
  model: {
    defaults: {
      tagName: 'mj-image',
      draggable: true,
      droppable: false,
      attributes: {
        'data-youtube': 'true',
        'src': '',
        'href': '',
        'alt': 'YouTube video preview',
        'video-url': '',
        'preview-hq': false,
        'width': '600px',
        'padding': '10px'
      },
      traits: [
        {
          type: 'text',
          name: 'video-url',
          label: 'YouTube URL',
          placeholder: 'https://youtu.be/...',
          changeProp: true
        },
        {
          type: 'text',
          name: 'alt',
          label: 'Alt Text'
        },
        {
          type: 'checkbox',
          name: 'preview-hq',
          label: 'HQ Thumbnail',
          changeProp: true
        }
      ]
    },
    
    init() {
      this.on('change:video-url change:preview-hq', this.updateVideo);
      
      // Initial check
      setTimeout(() => {
        const videoUrl = this.get('video-url');
        if (videoUrl) {
          this.updateVideo();
        }
      }, 100);
    },
    
    // Replace your updateVideo method with this one
updateVideo() {
  const videoUrl = this.get('video-url') || '';
  const useHQ = this.get('preview-hq');
  const videoId = getYouTubeId(videoUrl);
  if (videoId) {
    const quality = useHQ ? 'maxresdefault' : 'hqdefault';
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
    const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
    
    // Method 1: Update attributes
    const newAttrs = {
      ...this.getAttributes(),
      'src': thumbnailUrl,
      'href': watchUrl,
      'video-url': videoUrl
    };
    
    // Set attributes
    this.set('attributes', newAttrs);
    
    // Method 2: Also set src as a direct property
    this.set('src', thumbnailUrl);
    
    // Method 3: Force component to recognize the change
    this.trigger('change:src');
    this.trigger('change:attributes');
    
    // Method 4: If view exists, update DOM directly
    const view = this.getView();
    if (view && view.el) {
      view.el.src = thumbnailUrl;
      view.el.setAttribute('src', thumbnailUrl);
      view.el.setAttribute('href', watchUrl);
    }
    
    // Method 5: Force re-render
    if (this.view) {
      this.view.render();
    }
  } else {
    console.warn('Could not extract YouTube ID from URL:', videoUrl);
    
    // Set error image
    this.set('src', 'https://via.placeholder.com/600x400?text=Invalid+YouTube+URL');
    this.set('attributes', {
      ...this.getAttributes(),
      'src': 'https://via.placeholder.com/600x400?text=Invalid+YouTube+URL'
    });
  }
}
  }
});

// Block definition
editor.BlockManager.add('youtube-video-mjml', {
  label: 'YouTube Video',
  category: 'MJML Content',
  attributes: { class: 'fa fa-youtube-play' },
  content: `
    <mj-section padding="20px 0">
      <mj-column>
        <mj-image
          data-youtube="true"
          src="https://www.youtube.com/watch?v=6vTmdZ_kldI"
          video-url=""
          alt="YouTube video preview"
          width="600px"
        />
        <mj-text align="center" font-size="16px" font-weight="bold" padding-top="10px">
          Click image and add YouTube URL in properties panel
        </mj-text>
      </mj-column>
    </mj-section>
  `
});


          // Add MJML-specific blocks
          this.addMjmlBlocks(editor);

          // Mark editor as initialized
          this.editorInitialized = true;
          
          // Hide loading indicator after editor is fully initialized
          this.hideLoading();
        },
      });
    } catch (error) {
      console.error('Error creating editor:', error);
      this.hideLoading();
    }
  }

  // Add MJML-specific blocks
  private addMjmlBlocks(editor: any) {
    // MJML Header with logo
    editor.BlockManager.add('mjml-header', {
      label: 'Header with Logo',
      category: 'MJML Layout',
      attributes: { class: 'fa fa-header' },
      content: `
        <mj-section background-color="#4361ee" padding="20px">
          <mj-column width="25%">
            <mj-image src="https://via.placeholder.com/120x40/4361ee/ffffff?text=LOGO" alt="Logo" width="120px" />
          </mj-column>
          <mj-column width="50%">
            <mj-text align="center" color="#ffffff" font-size="24px" font-weight="bold">
              Your Company Name
            </mj-text>
          </mj-column>
          <mj-column width="25%"></mj-column>
        </mj-section>
      `
    });

    // MJML Button
    editor.BlockManager.add('mjml-button', {
      label: 'CTA Button',
      category: 'MJML Content',
      attributes: { class: 'fa fa-hand-pointer-o' },
      content: `
        <mj-section padding="20px 0">
          <mj-column>
            <mj-button 
              background-color="#4361ee" 
              color="#ffffff" 
              font-size="16px" 
              font-weight="bold"
              border-radius="4px"
              padding="12px 24px"
              href="#">
              Click Here
            </mj-button>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Social Media Links
    editor.BlockManager.add('mjml-social', {
      label: 'Social Media',
      category: 'MJML Content',
      attributes: { class: 'fa fa-share-alt' },
      content: `
        <mj-section padding="20px">
          <mj-column>
            <mj-text align="center" font-size="16px" padding-bottom="15px">
              Follow us on social media
            </mj-text>
            <mj-social>
              <mj-social-element name="facebook" href="#" />
              <mj-social-element name="twitter" href="#" />
              <mj-social-element name="linkedin" href="#" />
              <mj-social-element name="instagram" href="#" />
            </mj-social>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Product Showcase
    editor.BlockManager.add('mjml-product', {
      label: 'Product Card',
      category: 'MJML Content',
      attributes: { class: 'fa fa-shopping-cart' },
      content: `
        <mj-section background-color="#ffffff" padding="20px" border="1px solid #e0e0e0" border-radius="8px">
          <mj-column width="40%">
            <mj-image src="https://via.placeholder.com/300x200" alt="Product" />
          </mj-column>
          <mj-column width="60%" padding-left="20px">
            <mj-text font-size="18px" font-weight="bold" color="#333333">
              Product Name
            </mj-text>
            <mj-text font-size="14px" color="#666666" padding-top="10px">
              Product description goes here...
            </mj-text>
            <mj-text font-size="20px" font-weight="bold" color="#4361ee" padding-top="10px">
              $99.99
            </mj-text>
            <mj-button 
              background-color="#4361ee" 
              color="#ffffff" 
              font-weight="bold"
              border-radius="4px"
              padding="10px 20px"
              href="#"
              align="left">
              Buy Now
            </mj-button>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Newsletter Signup
    // MJML Newsletter Signup
editor.BlockManager.add('mjml-newsletter', {
  label: 'Newsletter Signup',
  category: 'MJML Content',
  attributes: { class: 'fa fa-newspaper-o' },
  content: `
    <mj-section background-color="#f8f9fa" padding="30px" border-radius="8px">
      <mj-column>
        <mj-text align="center" font-size="24px" font-weight="bold" color="#333333">
          Stay Updated!
        </mj-text>
        <mj-text align="center" font-size="16px" color="#666666" padding-top="15px">
          Subscribe to our newsletter for the latest updates and offers.
        </mj-text>
        <mj-text align="center" font-size="14px" color="#666666" padding-top="20px">
          Enter your email below to subscribe:
        </mj-text>
        <mj-button 
          background-color="#4361ee" 
          color="#ffffff" 
          font-weight="bold"
          border-radius="4px"
          padding="12px 24px"
          href="mailto:subscribe@company.com?subject=Newsletter Subscription"
          align="center">
          Subscribe Now
        </mj-button>
        <mj-text align="center" font-size="12px" color="#999999" padding-top="10px">
          Click the button above to subscribe via email
        </mj-text>
      </mj-column>
    </mj-section>
  `
});

    // MJML Divider
    editor.BlockManager.add('mjml-divider', {
      label: 'Divider',
      category: 'MJML Layout',
      attributes: { class: 'fa fa-minus' },
      content: `
        <mj-section padding="20px 0">
          <mj-column>
            <mj-divider border-color="#e0e0e0" border-width="1px" />
          </mj-column>
        </mj-section>
      `
    });

    // MJML Three Columns
    editor.BlockManager.add('mjml-three-columns', {
      label: 'Three Columns',
      category: 'MJML Layout',
      attributes: { class: 'fa fa-th-large' },
      content: `
        <mj-section padding="20px">
          <mj-group>
            <mj-column>
              <mj-image src="https://via.placeholder.com/150x100" alt="Feature 1" />
              <mj-text align="center" font-size="16px" font-weight="bold" color="#333333" padding-top="10px">
                Feature 1
              </mj-text>
              <mj-text align="center" font-size="14px" color="#666666">
                Description for feature 1
              </mj-text>
            </mj-column>
            <mj-column>
              <mj-image src="https://via.placeholder.com/150x100" alt="Feature 2" />
              <mj-text align="center" font-size="16px" font-weight="bold" color="#333333" padding-top="10px">
                Feature 2
              </mj-text>
              <mj-text align="center" font-size="14px" color="#666666">
                Description for feature 2
              </mj-text>
            </mj-column>
            <mj-column>
              <mj-image src="https://via.placeholder.com/150x100" alt="Feature 3" />
              <mj-text align="center" font-size="16px" font-weight="bold" color="#333333" padding-top="10px">
                Feature 3
              </mj-text>
              <mj-text align="center" font-size="14px" color="#666666">
                Description for feature 3
              </mj-text>
            </mj-column>
          </mj-group>
        </mj-section>
      `
    });

    // MJML Testimonial
    // MJML Testimonial
editor.BlockManager.add('mjml-testimonial', {
  label: 'Testimonial',
  category: 'MJML Content',
  attributes: { class: 'fa fa-quote-left' },
  content: `
    <mj-section background-color="#f8f9fa" padding="30px" border-left="4px solid #4361ee">
      <mj-column width="80px">
        <mj-image src="https://via.placeholder.com/60x60/4361ee/ffffff?text=JD" alt="Avatar" width="60px" border-radius="50%" />
      </mj-column>
      <mj-column>
        <mj-text font-style="italic" font-size="16px" color="#333333" line-height="1.5">
          "This product has completely transformed our business!"
        </mj-text>
        <mj-text font-size="14px" color="#666666" padding-top="15px">
          John Doe
        </mj-text>
        <mj-text font-size="14px" color="#666666">
          CEO, Company Name
        </mj-text>
      </mj-column>
    </mj-section>
  `
});

    // MJML Progress Bar
    editor.BlockManager.add('mjml-progress', {
      label: 'Progress Bar',
      category: 'MJML Content',
      attributes: { class: 'fa fa-tasks' },
      content: `
        <mj-section padding="20px">
          <mj-column>
            <mj-text font-weight="bold" color="#333333">
              Campaign Progress
            </mj-text>
            <mj-text padding="0">
              <div style="background-color: #e0e0e0; height: 20px; border-radius: 10px; overflow: hidden; margin-top: 10px;">
                <div style="background-color: #4361ee; height: 100%; width: 75%; border-radius: 10px; position: relative;">
                  <span style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: white; font-size: 12px; font-weight: bold;">75%</span>
                </div>
              </div>
            </mj-text>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Countdown Timer
    editor.BlockManager.add('mjml-countdown', {
      label: 'Countdown Timer',
      category: 'MJML Content',
      attributes: { class: 'fa fa-clock-o' },
      content: `
        <mj-section background-color="#ff4757" padding="30px" border-radius="8px">
          <mj-column>
            <mj-text align="center" color="#ffffff" font-size="24px" font-weight="bold">
              Limited Time Offer!
            </mj-text>
            <mj-spacer height="20px" />
            <mj-table color="#ffffff" align="center">
              <tr>
                <td style="background-color: rgba(255,255,255,0.2); padding: 15px; border-radius: 8px; text-align: center; min-width: 60px;">
                  <div style="font-size: 24px; font-weight: bold;">05</div>
                  <div style="font-size: 12px; text-transform: uppercase;">Days</div>
                </td>
                <td style="padding: 0 10px; font-size: 24px;">:</td>
                <td style="background-color: rgba(255,255,255,0.2); padding: 15px; border-radius: 8px; text-align: center; min-width: 60px;">
                  <div style="font-size: 24px; font-weight: bold;">12</div>
                  <div style="font-size: 12px; text-transform: uppercase;">Hours</div>
                </td>
                <td style="padding: 0 10px; font-size: 24px;">:</td>
                <td style="background-color: rgba(255,255,255,0.2); padding: 15px; border-radius: 8px; text-align: center; min-width: 60px;">
                  <div style="font-size: 24px; font-weight: bold;">30</div>
                  <div style="font-size: 12px; text-transform: uppercase;">Minutes</div>
                </td>
              </tr>
            </mj-table>
            <mj-text align="center" color="#ffffff" font-size="16px" padding-top="20px">
              Don't miss out on this amazing deal!
            </mj-text>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Enhanced Footer
    editor.BlockManager.add('mjml-footer', {
      label: 'Enhanced Footer',
      category: 'MJML Layout',
      attributes: { class: 'fa fa-footer' },
      content: `
        <mj-section background-color="#2c3e50" padding="40px 20px">
          <mj-column>
            <mj-text align="center" color="#ecf0f1" font-size="20px" font-weight="bold">
              Your Company
            </mj-text>
            <mj-text align="center" color="#ecf0f1" font-size="14px" line-height="1.6" padding-top="10px">
              123 Business St, City, State 12345
            </mj-text>
            <mj-text align="center" color="#ecf0f1" font-size="14px" line-height="1.6">
              Phone: +1 (555) 123-4567 | Email: contact@company.com
            </mj-text>
            <mj-divider border-color="#34495e" border-width="1px" padding="20px 0" />
            <mj-text align="center" color="#ecf0f1" font-size="12px">
              © 2025 Your Company. All rights reserved.
            </mj-text>
            <mj-text align="center" font-size="12px" padding-top="10px">
              <a href="#" style="color: #3498db; text-decoration: none; margin: 0 10px;">Privacy Policy</a>
              <a href="#" style="color: #3498db; text-decoration: none; margin: 0 10px;">Terms of Service</a>
              <a href="#" style="color: #3498db; text-decoration: none; margin: 0 10px;">Unsubscribe</a>
            </mj-text>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Hero Section
    editor.BlockManager.add('mjml-hero', {
      label: 'Hero Section',
      category: 'MJML Content',
      attributes: { class: 'fa fa-picture-o' },
      content: `
        <mj-hero 
          mode="fluid-height" 
          background-width="600px" 
          background-height="400px"
          background-url="https://via.placeholder.com/600x400"
          background-color="#4361ee"
          padding="100px 0">
          <mj-text align="center" color="#ffffff" font-size="36px" font-weight="bold">
            Welcome to Our Newsletter
          </mj-text>
          <mj-button 
            background-color="#ffffff" 
            color="#4361ee" 
            font-size="18px" 
            font-weight="bold"
            border-radius="25px"
            padding="15px 30px"
            href="#">
            Get Started
          </mj-button>
        </mj-hero>
      `
    });

    // MJML Accordion
    editor.BlockManager.add('mjml-accordion', {
      label: 'Accordion/FAQ',
      category: 'MJML Content',
      attributes: { class: 'fa fa-bars' },
      content: `
        <mj-section padding="20px">
          <mj-column>
            <mj-accordion>
              <mj-accordion-element icon-wrapped-url="https://i.imgur.com/Xvw0vjq.png" icon-unwrapped-url="https://i.imgur.com/KKHenWa.png">
                <mj-accordion-title font-size="16px" color="#333333">Question 1?</mj-accordion-title>
                <mj-accordion-text font-size="14px" color="#666666">
                  Answer to question 1 goes here...
                </mj-accordion-text>
              </mj-accordion-element>
              <mj-accordion-element icon-wrapped-url="https://i.imgur.com/Xvw0vjq.png" icon-unwrapped-url="https://i.imgur.com/KKHenWa.png">
                <mj-accordion-title font-size="16px" color="#333333">Question 2?</mj-accordion-title>
                <mj-accordion-text font-size="14px" color="#666666">
                  Answer to question 2 goes here...
                </mj-accordion-text>
              </mj-accordion-element>
            </mj-accordion>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Carousel
    editor.BlockManager.add('mjml-carousel', {
      label: 'Image Carousel',
      category: 'MJML Content',
      attributes: { class: 'fa fa-film' },
      content: `
        <mj-section>
          <mj-column>
            <mj-carousel>
              <mj-carousel-image src="https://via.placeholder.com/600x400/4361ee/ffffff?text=Slide+1" />
              <mj-carousel-image src="https://via.placeholder.com/600x400/ff4757/ffffff?text=Slide+2" />
              <mj-carousel-image src="https://via.placeholder.com/600x400/2ecc71/ffffff?text=Slide+3" />
            </mj-carousel>
          </mj-column>
        </mj-section>
      `
    });

    // MJML Navbar
    editor.BlockManager.add('mjml-navbar', {
      label: 'Navigation Bar',
      category: 'MJML Layout',
      attributes: { class: 'fa fa-navicon' },
      content: `
        <mj-section background-color="#4361ee">
          <mj-column>
            <mj-navbar base-url="#" hamburger="hamburger" ico-color="#ffffff">
              <mj-navbar-link href="#" color="#ffffff" font-weight="bold">Home</mj-navbar-link>
              <mj-navbar-link href="#" color="#ffffff" font-weight="bold">Products</mj-navbar-link>
              <mj-navbar-link href="#" color="#ffffff" font-weight="bold">Services</mj-navbar-link>
              <mj-navbar-link href="#" color="#ffffff" font-weight="bold">Contact</mj-navbar-link>
            </mj-navbar>
          </mj-column>
        </mj-section>
      `
    });

    // Add custom component types for better property management
    this.addCustomMjmlComponentTypes(editor);
  }

  // Add custom MJML component types with enhanced trait management
  private addCustomMjmlComponentTypes(editor: any) {
    // Enhanced MJML button component with dynamic updates
    editor.Components.addType('mj-button-custom', {
      extend: 'mj-button',
      model: {
        defaults: {
          tagName: 'mj-button',
          traits: [
            { name: 'href', label: 'Link URL', type: 'text' },
            { name: 'inner-text', label: 'Button Text', type: 'text' },
            { name: 'background-color', label: 'Background Color', type: 'color' },
            { name: 'color', label: 'Text Color', type: 'color' },
            { name: 'border-radius', label: 'Border Radius', type: 'text' },
            { name: 'padding', label: 'Padding', type: 'text' },
            { name: 'font-size', label: 'Font Size', type: 'text' },
            { name: 'font-weight', label: 'Font Weight', type: 'select', options: [
              { value: 'normal', name: 'Normal' },
              { value: 'bold', name: 'Bold' },
              { value: '600', name: 'Semi Bold' }
            ]},
            { name: 'align', label: 'Alignment', type: 'select', options: [
              { value: 'left', name: 'Left' },
              { value: 'center', name: 'Center' },
              { value: 'right', name: 'Right' }
            ]}
          ]
        }
      }
    });

    // Enhanced MJML text component
    editor.Components.addType('mj-text-custom', {
      extend: 'mj-text',
      model: {
        defaults: {
          tagName: 'mj-text',
          traits: [
            { name: 'color', label: 'Text Color', type: 'color' },
            { name: 'font-size', label: 'Font Size', type: 'text' },
            { name: 'font-weight', label: 'Font Weight', type: 'select', options: [
              { value: 'normal', name: 'Normal' },
              { value: 'bold', name: 'Bold' },
              { value: '300', name: 'Light' },
              { value: '600', name: 'Semi Bold' }
            ]},
            { name: 'align', label: 'Text Align', type: 'select', options: [
              { value: 'left', name: 'Left' },
              { value: 'center', name: 'Center' },
              { value: 'right', name: 'Right' },
              { value: 'justify', name: 'Justify' }
            ]},
            { name: 'line-height', label: 'Line Height', type: 'text' },
            { name: 'padding', label: 'Padding', type: 'text' },
            { name: 'letter-spacing', label: 'Letter Spacing', type: 'text' }
          ]
        }
      }
    });

    // Enhanced MJML image component
    editor.Components.addType('mj-image-custom', {
      extend: 'mj-image',
      model: {
        defaults: {
          tagName: 'mj-image',
          traits: [
            { name: 'src', label: 'Image URL', type: 'text' },
            { name: 'alt', label: 'Alt Text', type: 'text' },
            { name: 'href', label: 'Link URL', type: 'text' },
            { name: 'width', label: 'Width', type: 'text' },
            { name: 'border-radius', label: 'Border Radius', type: 'text' },
            { name: 'padding', label: 'Padding', type: 'text' },
            { name: 'align', label: 'Alignment', type: 'select', options: [
              { value: 'left', name: 'Left' },
              { value: 'center', name: 'Center' },
              { value: 'right', name: 'Right' }
            ]}
          ]
        }
      }
    });

    // Enhanced MJML section component
    editor.Components.addType('mj-section-custom', {
      extend: 'mj-section',
      model: {
        defaults: {
          tagName: 'mj-section',
          traits: [
            { name: 'background-color', label: 'Background Color', type: 'color' },
            { name: 'background-url', label: 'Background Image URL', type: 'text' },
            { name: 'padding', label: 'Padding', type: 'text' },
            { name: 'border', label: 'Border', type: 'text' },
            { name: 'border-radius', label: 'Border Radius', type: 'text' },
            { name: 'full-width', label: 'Full Width', type: 'checkbox' }
          ]
        }
      }
    });
  }

  ngOnInit() {
    this.isLoading = true;
    
    this.loadingTimeout = window.setTimeout(() => {
      this.hideLoading();
    }, 15000);
    
    this.loadTags();
  }

  hideLoading() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = undefined;
    }
    
    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  private isEditorReady(): boolean {
    if (!this.editor || !this.editorInitialized) {
      console.warn('Editor not initialized yet');
      return false;
    }
    return true;
  }

  async onTagSelect(value: any) {
    if (!value) return;
    
    const findExist = this.recent.find((x: any) => x.id === value)
    const tag = this.allFields.find((field: any) => field.id === parseInt(value.id));
    if (!findExist) {
      const response = await firstValueFrom(this.advanceSearch.lastUsedUpdate({}, value.id));
      if (response) {
        if (this.recent.length === 5) {
          this.recent.pop();
          this.recent.unshift(tag);
        } else {
          this.recent.unshift(tag);
        }
      }
    }
    const entitySuffix = tag.entitySearch ? `.${tag.entitySearch}` : '';
    const convertValue = `[[${tag.entitySearch ? tag.relationField : tag.field}${entitySuffix}]]`;
    this.insertTag(convertValue);
  }

  insertTag(value: any) {
    if (!value || !this.isEditorReady()) return;
  
    // First try to insert via TinyMCE if it's active
    if (this.insertViaTinyMCE(value)) {
      return;
    }
  
    const selectedComponent = this.editor.getSelected();
    
    if (selectedComponent) {
      const tagName = selectedComponent.get('tagName');
      
      if (tagName === 'mj-text') {
        this.appendToMjText(selectedComponent, value);
      } else if (tagName === 'mj-column') {
        this.addTextToColumn(selectedComponent, value);
      } else if (tagName === 'mj-section') {
        this.addTextToSection(selectedComponent, value);
      } else {
        // For other components, find appropriate parent
        this.addTextNearComponent(selectedComponent, value);
      }
    } else {
      // No selection, add to body
      this.addTextToBody(value);
    }
  }
  
  private insertViaTinyMCE(value: string): boolean {
    try {
      if (typeof window !== 'undefined' && (window as any).tinymce) {
        const tinymce = (window as any).tinymce;
        if (tinymce.activeEditor && !tinymce.activeEditor.isHidden()) {
          tinymce.activeEditor.insertContent(value);
          return true;
        }
      }
    } catch (error) {
      console.warn('TinyMCE insert failed:', error);
    }
    return false;
  }
  
  private appendToMjText(mjTextComponent: any, value: string) {
    try {
      // Get existing text
      let existingText = '';
      
      // Get from view first
      if (mjTextComponent.view && mjTextComponent.view.el) {
        existingText = mjTextComponent.view.el.textContent || '';
      }
      
      // Clean up whitespace
      existingText = existingText.trim();
      
      // If view text already contains the value, don't add it again
      if (existingText.includes(value)) {
        return;
      }
      
      // Combine text with a space if needed
      const needsSpace = existingText && !existingText.endsWith(' ');
      const combinedText = existingText + (needsSpace ? ' ' : '') + value;
      // Option 1: Use append instead of components() to avoid the error
      const components = mjTextComponent.components();
      components.reset(); // Clear existing
      mjTextComponent.append(combinedText); // Add as new content
      
      // Option 2: If you still get errors, try this approach:
      // mjTextComponent.set('content', combinedText, { silent: true });
      // mjTextComponent.view?.render();
      
    } catch (error) {
      console.error('Error in appendToMjText:', error);
      // Simple fallback
      mjTextComponent.append(' ' + value);
    }
  }
  
  
  
  private addTextToColumn(column: any, value: string) {
    try {
      const textComponents = column.find('mj-text');
      
      if (textComponents && textComponents.length > 0) {
        // Add to last text component
        this.appendToMjText(textComponents[textComponents.length - 1], value);
      } else {
        // Create new mj-text - DON'T set content twice!
        column.append({
          tagName: 'mj-text',
          type: 'mj-text',
          content: value
          // Remove the components array - content is enough
        });
      }
    } catch (error) {
      console.error('Error adding text to column:', error);
    }
  }
  
  private addTextToSection(section: any, value: string) {
    try {
      const columns = section.find('mj-column');
      
      if (columns && columns.length > 0) {
        this.addTextToColumn(columns[0], value);
      } else {
        // Create column first
        section.append({
          tagName: 'mj-column',
          type: 'mj-column',
          components: [{
            tagName: 'mj-text',
            type: 'mj-text',
            content: value
            // Remove the nested components array
          }]
        });
      }
    } catch (error) {
      console.error('Error adding text to section:', error);
    }
  }
  
  private addTextNearComponent(component: any, value: string) {
    try {
      let parent = component.parent();
      
      while (parent) {
        const parentTag = parent.get('tagName');
        
        if (parentTag === 'mj-column') {
          this.addTextToColumn(parent, value);
          return;
        } else if (parentTag === 'mj-section') {
          this.addTextToSection(parent, value);
          return;
        }
        
        parent = parent.parent();
      }
      
      // If no suitable parent found, add to body
      this.addTextToBody(value);
    } catch (error) {
      console.error('Error adding text near component:', error);
      this.addTextToBody(value);
    }
  }
  
  private addTextToBody(value: string) {
    try {
      const wrapper = this.editor.getWrapper();
      const body = wrapper.find('mj-body')[0] || wrapper;
      const sections = body.find('mj-section');
      
      if (sections && sections.length > 0) {
        // Add to last section
        const lastSection = sections[sections.length - 1];
        this.addTextToSection(lastSection, value);
      } else {
        // Create new section
        body.append({
          tagName: 'mj-section',
          components: [{
            tagName: 'mj-column',
            components: [{
              tagName: 'mj-text',
              content: value
              // Remove the nested components array
            }]
          }]
        });
      }
    } catch (error) {
      console.error('Error adding text to body:', error);
    }
  }

  openTemplatesPanel() {
    if (!this.isEditorReady()) return;
    
    this.editor.runCommand('studio:layoutToggle', {
      id: 'my-templates-panel',
      header: false,
      placer: { 
        type: 'dialog', 
        title: 'Choose a template for your project', 
        size: 'l' 
      },
      layout: {
        type: 'panelTemplates',
        content: { itemsPerRow: 3 },
        onSelect: (opts: { loadTemplate: Function; template: any }) => {
          opts.loadTemplate(opts.template);
          this.editor.runCommand('studio:layoutRemove', { id: 'my-templates-panel' });
        }
      }
    });
  }

  getTemplate() {
    if (!this.isEditorReady()) return null;
    
    // Get MJML content
    const mjml = this.editor.getHtml();
    
    // For MJML, we need to compile it to HTML
    // Note: This would typically require an MJML compiler
    // For now, we'll save the MJML source
    const fullMjml = mjml;

    const blob = new Blob([fullMjml], { type: 'text/plain' });
    const file = new File([blob], 'email-template.mjml', { type: 'text/plain' });
    return file;
  }

  // Method to get compiled HTML from MJML
  async getCompiledHtml() {
    if (!this.isEditorReady()) return null;
    
    const mjml = this.editor.getHtml();
    
    // This would typically call an MJML compilation service
    // For example, using mjml2html library or an API endpoint
    try {
      // Example API call to compile MJML
      const response = await fetch('/api/compile-mjml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mjml })
      });
      
      const { html } = await response.json();
      return html;
    } catch (error) {
      console.error('Error compiling MJML:', error);
      return null;
    }
  }

  // Method to preview email in different clients
  async previewEmail(clientType: 'gmail' | 'outlook' | 'apple' | 'mobile' = 'gmail') {
    if (!this.isEditorReady()) return;
    
    // Get compiled HTML from MJML
    const html = await this.getCompiledHtml();
    
    if (!html) {
      console.error('Could not compile MJML to HTML');
      return;
    }
    
    // Open preview in new window
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write(html);
      previewWindow.document.close();
    }
  }

  // Method to validate MJML content
  validateMjmlContent(): { isValid: boolean; warnings: string[]; errors: string[] } {
    if (!this.isEditorReady()) {
      return { isValid: false, warnings: [], errors: ['Editor not ready'] };
    }
    
    const mjml = this.editor.getHtml();
    const warnings: string[] = [];
    const errors: string[] = [];
    
    // Basic MJML validation
    if (!mjml.includes('<mjml>')) {
      errors.push('Missing <mjml> root element');
    }
    
    if (!mjml.includes('<mj-body>')) {
      errors.push('Missing <mj-body> element');
    }
    
    // Check for common MJML issues
    if (mjml.includes('style=') && !mjml.includes('inline-style=')) {
      warnings.push('Use inline-style attribute instead of style for MJML components');
    }
    
    // Check for proper nesting
    const mjSectionRegex = /<mj-section[^>]*>(?:(?!<\/mj-section>).)*<mj-section/g;
    if (mjSectionRegex.test(mjml)) {
      errors.push('Nested mj-section elements are not allowed');
    }
    
    // Check column widths
    const columnRegex = /<mj-column[^>]*width="([^"]+)"/g;
    let match;
    let totalWidth = 0;
    while ((match = columnRegex.exec(mjml)) !== null) {
      const width = match[1];
      if (width.includes('%')) {
        totalWidth += parseFloat(width);
      }
    }
    
    if (totalWidth > 100) {
      warnings.push(`Total column width exceeds 100% (${totalWidth}%)`);
    }
    
    const isValid = errors.length === 0;
    return { isValid, warnings, errors };
  }

  loadTemplate(file: any) {
    this.isLoading = true;
    
    if (this.applyTimer) {
      clearTimeout(this.applyTimer);
    }
    
    this.applyTimer = window.setTimeout(() => {
      if (!this.isEditorReady()) {
        this.isLoading = false;
        return;
      }
      
      if (!file) {
        // Reset to default MJML template
        this.editor.setComponents(`
          <mjml>
            <mj-head>
              <mj-attributes>
                <mj-all font-family="Arial, sans-serif" />
                <mj-text font-size="14px" color="#333333" line-height="1.6" />
                <mj-section background-color="#ffffff" />
              </mj-attributes>
            </mj-head>
            <mj-body background-color="#f4f4f4">
              <mj-section background-color="#4361ee" padding="20px">
                <mj-column>
                  <mj-text align="center" color="#ffffff" font-size="24px" font-weight="bold">
                    Email Template
                  </mj-text>
                </mj-column>
              </mj-section>
              <mj-section padding="20px">
                <mj-column>
                  <mj-text>
                    Start building your email template here...
                  </mj-text>
                </mj-column>
              </mj-section>
              <mj-section background-color="#f8f9fa" padding="20px">
                <mj-column>
                  <mj-text align="center" font-size="12px" color="#666666">
                    Footer content
                  </mj-text>
                </mj-column>
              </mj-section>
            </mj-body>
          </mjml>
        `);
        this.isLoading = false;
        return;
      }
      
      try {
        // For MJML templates, load directly
        if (file.includes('<mjml>')) {
          this.editor.setComponents(file);
        } else {
          // If it's HTML, try to convert to MJML structure
          console.warn('HTML template detected. Manual conversion to MJML may be required.');
          // You could implement HTML to MJML conversion here
        }
      } catch (error) {
        console.error('Error loading template:', error);
      } finally {
        this.isLoading = false;
      }
    }, 1000);
  }

  async onSettingsClick(event: Event, item: any, menuTrigger?: MatMenuTrigger) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(SettingsDialogComponent, {
      width: '500px',
      data: {
        title: `Default value for ${item.label}`,
        defaultValue: item?.advanceSearchTypeSettings?.default
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result !== undefined) {
        const response = await firstValueFrom(
          this.advanceSearch.createDefaultValue({
            defaultValue: result?.defaultValue,
            advance_search_type_id: item.id
          })
        );

        if (response) {
          const index = this.otherFields.findIndex((field: any) => field.id === item.id);

          if (index !== -1) {
            this.otherFields[index] = {
              ...this.otherFields[index],
              advanceSearchTypeSettings: {
                ...this.otherFields[index].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };
            this.otherFields = [...this.otherFields];
          }

          const leadIndex = this.leadTags.findIndex((field: any) => field.id === item.id);
          
          if (leadIndex !== -1) {
            this.leadTags[leadIndex] = {
              ...this.leadTags[leadIndex],
              advanceSearchTypeSettings: {
                ...this.leadTags[leadIndex].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };
            this.leadTags = [...this.leadTags];
          }
        }

        if (menuTrigger) {
          menuTrigger.closeMenu();
        }
      }
    });
  }

  resetEditor() {
    if (!this.isEditorReady()) return;
    
    this.isLoading = true;
    
    setTimeout(() => {
      try {
        this.editor.setComponents('');
        this.editor.setStyle('');
        this.editor.UndoManager.clear();
        this.editor.CssComposer.clear();
        this.editor.SelectorManager.clear();
      } catch (error) {
        console.error('Error resetting editor:', error);
      } finally {
        this.isLoading = false;
      }
    }, 300);
  }
  
  ngOnDestroy(): void {
    if (this.applyTimer) {
      clearTimeout(this.applyTimer);
    }
    
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    
    if (this.editor) {
      try {
        this.editor.destroy();
      } catch (error) {
        console.error('Error destroying editor:', error);
      }
      this.editor = null;
    }
    
    if (this.linkElement) {
      this.renderer.removeChild(document.head, this.linkElement);
    }
  }
}